import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Text, Button, Table, Badge, Group, Modal, Stack, TextInput, Textarea,
  Select, ActionIcon, Tooltip, Center, Loader, Tabs, NumberInput, Timeline, Checkbox,
  SimpleGrid, Paper, Autocomplete,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  IconPlus, IconCheck, IconBrandLine, IconWorld, IconEye, IconX, IconMessageCircle, IconFlame, IconTool, IconEdit,
} from '@tabler/icons-react'
import { useBreakpoint } from '../hooks/useBreakpoint'
import {
  getWorkOrders, createWorkOrder, closeWorkOrder, claimWorkOrder, getInventoryItems,
  getWorkOrderUpdates, addWorkOrderUpdate, cancelWorkOrder,
  getWorkOrderMaterials, addWorkOrderMaterial, removeWorkOrderMaterial,
  getLocations, updateWorkOrder,
} from '../api/client'
import { useWorkOrderMeta } from '../hooks/useWorkOrderMeta'

const STATUS_COLOR: Record<string, string> = { OPEN: 'orange', IN_PROGRESS: 'blue', DONE: 'lime', CANCELLED: 'gray' }

const EMPTY_WO = { title: '', description: '', location: '', category: 'OTHER', departmentCode: '', isUrgent: false }

export default function WorkOrdersPage() {
  const { t } = useTranslation()
  const { isMobile, isSmall } = useBreakpoint()
  const [tab, setTab] = useState<string | null>('OPEN')
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'all'>('today')
  const [items, setItems] = useState<any[]>([])
  const [allItems, setAllItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [invItems, setInvItems] = useState<any[]>([])
  const [editingWo, setEditingWo] = useState<any>(null)
  const [editForm, setEditForm] = useState({ title: '', description: '', location: '', category: 'OTHER', departmentCode: '', isUrgent: false })
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false)
  const [saving, setSaving] = useState(false)
  const [locations, setLocations] = useState<string[]>([])
  const { catLabel, deptLabel, categoryOptions, departmentOptions, deptOfCategory, fmtDateTime } = useWorkOrderMeta()

  // ── Filters (กรองฝั่ง client จากรายการที่ดึงมาแล้วตาม tab สถานะ) ──────────
  const [searchText, setSearchText] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [departmentFilter, setDepartmentFilter] = useState<string | null>(null)
  const [urgentOnly, setUrgentOnly] = useState(false)
  const [sourceFilter, setSourceFilter] = useState<string | null>(null)

  const [createOpened, { open: openCreate, close: closeCreateModal }] = useDisclosure(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({ ...EMPTY_WO })

  const [closeOpened, { open: openCloseModal, close: closeCloseModal }] = useDisclosure(false)
  const [closingWo, setClosingWo] = useState<any>(null)
  const [closeItemId, setCloseItemId] = useState<string | null>(null)
  const [closeQty, setCloseQty] = useState<number>(1)
  const [closing, setClosing] = useState(false)

  const [cancelOpened, { open: openCancelModal, close: closeCancelModal }] = useDisclosure(false)
  const [cancellingWo, setCancellingWo] = useState<any>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelling, setCancelling] = useState(false)

  const [detailOpened, { open: openDetail, close: closeDetailModal }] = useDisclosure(false)
  const [detailWo, setDetailWo] = useState<any>(null)
  const [detailUpdates, setDetailUpdates] = useState<any[]>([])
  const [detailMaterials, setDetailMaterials] = useState<any[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [addingNote, setAddingNote] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await getWorkOrders(undefined)  // ดึงทั้งหมดเสมอ count ถูก
      setAllItems(data)
      setItems(tab === 'ALL' ? data : data.filter((x: any) => x.status === tab))
    } catch {
      notifications.show({ color: 'red', message: t('common.error') })
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])  // โหลดครั้งเดียว
  useEffect(() => {
    setItems(tab === 'ALL' ? allItems : allItems.filter((x: any) => x.status === tab))
  }, [tab, allItems])
  useEffect(() => { getInventoryItems().then(r => setInvItems(r.data)).catch(() => {}) }, [])
  useEffect(() => {
    getLocations().then(r => setLocations((r.data ?? []).filter((l: any) => l.isActive !== false).map((l: any) => l.name ?? l.code ?? '').filter(Boolean))).catch(() => {})
  }, [])

  // ใช้ timezone ไทย (+7) ในการเปรียบเทียบวันที่
  const todayStr = new Date(Date.now() + 7 * 60 * 60 * 1000).toISOString().slice(0, 10)
  const weekAgo = new Date(Date.now() + 7 * 60 * 60 * 1000 - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  // allItems กรองด้วย dateFilter เพื่อให้ card count ถูกต้อง
  // helper แปลง createdAt UTC → วันที่ไทย
  const toThaiDate = (dt: string) =>
    dt ? new Date(new Date(dt).getTime() + 7*60*60*1000).toISOString().slice(0, 10) : ''

  const dateFilteredItems = allItems.filter((item: any) => {
    const createdDate = toThaiDate(item.createdAt)
    const isActive = item.status === 'OPEN' || item.status === 'IN_PROGRESS'
    if (dateFilter === 'today') {
      // แสดงงานวันนี้ + งานค้างที่ยังไม่จบ
      return createdDate === todayStr || isActive
    }
    if (dateFilter === 'week') {
      return createdDate >= weekAgo
    }
    return true // 'all'
  })

  const filteredItems = items.filter((wo) => {
    // WO ที่ยัง OPEN/IN_PROGRESS แสดงเสมอ ไม่ว่าวันไหน
    if (dateFilter === 'today') {
      const createdDate = toThaiDate(wo.createdAt)
      const isActive = wo.status === 'OPEN' || wo.status === 'IN_PROGRESS'
      if (!isActive && createdDate !== todayStr) return false
    } else if (dateFilter === 'week') {
      const createdDate = toThaiDate(wo.createdAt)
      if (createdDate < weekAgo) return false
    }
    if (categoryFilter && wo.category !== categoryFilter) return false
    if (departmentFilter && wo.departmentCode !== departmentFilter) return false
    if (sourceFilter && wo.source !== sourceFilter) return false
    if (urgentOnly && !wo.isUrgent) return false
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase()
      const haystack = `${wo.title} ${wo.location ?? ''} ${wo.description ?? ''} ${catLabel(wo.category)} ${wo.departmentCode ? deptLabel(wo.departmentCode) + ' ' + wo.departmentCode : ''}`.toLowerCase()
      if (!haystack.includes(q)) return false
    }
    return true
  })

  const handleOpenEdit = (wo: any) => {
    setEditingWo(wo)
    setEditForm({ title: wo.title ?? '', description: wo.description ?? '', location: wo.location ?? '', category: wo.category ?? 'OTHER', departmentCode: wo.departmentCode ?? '', isUrgent: !!wo.isUrgent })
    openEdit()
  }

  const handleSaveEdit = async () => {
    if (!editingWo) return
    setSaving(true)
    try {
      await updateWorkOrder(editingWo.id, editForm)
      notifications.show({ color: 'teal', message: `✅ ${t('workOrders.editTitle')} #${editingWo.id}` })
      closeEdit()
      load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'เกิดข้อผิดพลาด' })
    } finally { setSaving(false) }
  }

  const handleCreate = async () => {
    if (!form.title) {
      notifications.show({ color: 'red', message: t('common.required') }); return
    }
    setCreating(true)
    try {
      await createWorkOrder(form)
      notifications.show({ color: 'lime', message: t('common.success') })
      closeCreateModal(); setForm({ ...EMPTY_WO }); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    } finally { setCreating(false) }
  }

  const handleClaim = async (wo: any) => {
    try {
      await claimWorkOrder(wo.id)
      notifications.show({ color: 'lime', message: t('common.success') })
      load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    }
  }

  const openClose = (wo: any) => {
    setClosingWo(wo)
    setCloseItemId(wo.consumableItemId ? String(wo.consumableItemId) : null)
    setCloseQty(wo.consumableQty ?? 1)
    openCloseModal()
  }

  const handleClose = async () => {
    if (!closingWo) return
    setClosing(true)
    try {
      await closeWorkOrder(closingWo.id, closeItemId ? { consumableItemId: +closeItemId, consumableQty: closeQty } : {})
      notifications.show({ color: 'lime', message: t('common.success') })
      closeCloseModal(); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    } finally { setClosing(false) }
  }

  const openCancel = (wo: any) => {
    setCancellingWo(wo)
    setCancelReason('')
    openCancelModal()
  }

  const handleCancel = async () => {
    if (!cancellingWo || !cancelReason.trim()) {
      notifications.show({ color: 'red', message: t('common.required') }); return
    }
    setCancelling(true)
    try {
      await cancelWorkOrder(cancellingWo.id, cancelReason.trim())
      notifications.show({ color: 'lime', message: t('common.success') })
      closeCancelModal(); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    } finally { setCancelling(false) }
  }

  const openDetails = async (wo: any) => {
    setDetailWo(wo)
    setDetailUpdates([])
    setDetailMaterials([])
    setNewNote('')
    openDetail()
    setDetailLoading(true)
    try {
      const [updRes, matRes] = await Promise.all([
        getWorkOrderUpdates(wo.id),
        getWorkOrderMaterials(wo.id),
      ])
      setDetailUpdates(updRes.data)
      setDetailMaterials(matRes.data)
    } catch {
      notifications.show({ color: 'red', message: t('common.error') })
    } finally { setDetailLoading(false) }
  }

  const handleAddNote = async () => {
    if (!detailWo || !newNote.trim()) return
    setAddingNote(true)
    try {
      await addWorkOrderUpdate(detailWo.id, newNote.trim())
      const { data } = await getWorkOrderUpdates(detailWo.id)
      setDetailUpdates(data)
      setNewNote('')
      load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    } finally { setAddingNote(false) }
  }

  return (
    <Box p={isSmall ? 16 : 32}>
      <Group justify="space-between" mb={isMobile ? 'md' : 'xl'} wrap="nowrap">
        <Box style={{ minWidth: 0 }}>
          <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px' }}>
            {t('workOrders.title')}
          </Text>
          {!isMobile && <Text size="sm" c="dimmed">{t('workOrders.subtitle')}</Text>}
        </Box>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}
          style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
          {t('workOrders.newButton')}
        </Button>
      </Group>

      {/* ── Date Filter ── */}
      <Group gap="xs" mb="sm">
        {([
          { value: 'today', label: '📅 วันนี้' },
          { value: 'week',  label: '📆 7 วัน' },
          { value: 'all',   label: '🗂️ ทั้งหมด' },
        ] as const).map(d => (
          <Button key={d.value} size="xs"
            variant={dateFilter === d.value ? 'filled' : 'default'}
            style={dateFilter === d.value ? { background: 'var(--brand-primary,#0f172a)', color: '#fff' } : {}}
            onClick={() => setDateFilter(d.value)}>
            {d.label}
          </Button>
        ))}
        <Text size="xs" c="dimmed" mt={4}>* งาน OPEN/กำลังทำ แสดงเสมอแม้ข้ามวัน</Text>
      </Group>

      {/* ── Status Filter Cards ── */}
      <SimpleGrid cols={{ base: 3, sm: 5 }} spacing="xs" mb="md">
        {[
          { value: 'OPEN',        label: t('workOrders.statusOpen'),        color: '#f97316', bg: '#fff7ed', icon: '🕐' },
          { value: 'IN_PROGRESS', label: t('workOrders.statusInProgress'),  color: '#3b82f6', bg: '#eff6ff', icon: '🔧' },
          { value: 'DONE',        label: t('workOrders.statusDone'),         color: '#22c55e', bg: '#f0fdf4', icon: '✅' },
          { value: 'CANCELLED',   label: t('workOrders.statusCancelled'),    color: '#94a3b8', bg: '#f8fafc', icon: '🚫' },
          { value: 'ALL',         label: t('workOrders.statusAll'),           color: '#6366f1', bg: '#eef2ff', icon: '📋' },
        ].map(s => {
          const count = s.value === 'ALL' ? dateFilteredItems.length : dateFilteredItems.filter((w: any) => w.status === s.value).length
          const active = tab === s.value
          return (
            <Paper key={s.value} withBorder onClick={() => setTab(s.value)} style={{
              cursor: 'pointer', padding: '10px 8px', borderRadius: 10, textAlign: 'center',
              background: active ? s.color : s.bg,
              borderColor: active ? s.color : '#e2e8f0',
              transition: 'all 0.15s',
              boxShadow: active ? `0 2px 8px ${s.color}44` : 'none',
            }}>
              <Text size="lg">{s.icon}</Text>
              <Text size="xs" fw={700} style={{ color: active ? '#fff' : s.color }} mt={2}>{count}</Text>
              <Text size="xs" style={{ color: active ? '#ffffffcc' : '#64748b' }} mt={1} lineClamp={1}>{s.label}</Text>
            </Paper>
          )
        })}
      </SimpleGrid>

      <Group gap="sm" mb="md" wrap="wrap">
        <TextInput
          placeholder={t('workOrders.searchPlaceholder','Search title / location...')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
          styles={{ input: { borderColor: '#e2e8f0' } }}
        />
        <Select
          placeholder={t('workOrders.colDepartment')}
          clearable searchable
          nothingFoundMessage={t('workOrders.noMatch')}
          data={departmentOptions}
          value={departmentFilter}
          onChange={(v) => { setDepartmentFilter(v); if (v && categoryFilter && deptOfCategory(categoryFilter) && deptOfCategory(categoryFilter) !== v) setCategoryFilter(null) }}
          style={{ width: 170 }}
        />
        <Select
          placeholder={t('workOrders.colCategory')}
          clearable searchable
          nothingFoundMessage={t('workOrders.noMatch')}
          data={categoryOptions(departmentFilter, categoryFilter)}
          value={categoryFilter}
          onChange={setCategoryFilter}
          style={{ width: 180 }}
        />
        <Select
          placeholder={t('workOrders.colSource')}
          clearable
          data={[{ value: 'LINE', label: 'LINE' }, { value: 'WEB', label: 'Web' }]}
          value={sourceFilter}
          onChange={setSourceFilter}
          style={{ width: 130 }}
        />
        <Checkbox
          label={t('workOrders.urgentOnlyFilter','Urgent only')}
          checked={urgentOnly}
          onChange={(e) => setUrgentOnly(e.currentTarget.checked)}
        />
        {(searchText || categoryFilter || departmentFilter || sourceFilter || urgentOnly) && (
          <Button variant="subtle" color="gray" size="xs" leftSection={<IconX size={14} />}
            onClick={() => { setSearchText(''); setCategoryFilter(null); setDepartmentFilter(null); setSourceFilter(null); setUrgentOnly(false) }}>
            {t('common.clearFilters','Clear filters')}
          </Button>
        )}
      </Group>

      {loading ? (
        <Center py={60}><Loader /></Center>
      ) : (
        <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', overflow: 'hidden' }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead style={{ background: '#f8f7f4' }}>
              <Table.Tr>
                <Table.Th>#</Table.Th>
                <Table.Th>{t('workOrders.colTitle')}</Table.Th>
                <Table.Th>{t('workOrders.colLocation')}</Table.Th>
                <Table.Th>{t('workOrders.colDepartment')}</Table.Th>
                <Table.Th>{t('workOrders.colCategory')}</Table.Th>
                <Table.Th>{t('workOrders.colCreated')}</Table.Th>
                <Table.Th>{t('workOrders.colSource')}</Table.Th>
                <Table.Th>{t('workOrders.colStatus')}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredItems.map((wo) => (
                <Table.Tr key={wo.id} style={{ cursor: 'pointer' }} onClick={() => openDetails(wo)}>
                  <Table.Td><Text size="sm" c="dimmed">#{wo.id}</Text></Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      {wo.isUrgent && <Tooltip label={t('workOrders.urgentLabel')}><IconFlame size={16} color="#ef4444" /></Tooltip>}
                      <Text size="sm" fw={600} lineClamp={1} maw={260}>{wo.title}</Text>
                    </Group>
                    {wo.reportedByName && <Text size="xs" c="dimmed">{t('workOrders.reportedBy')} {wo.reportedByName}</Text>}
                  </Table.Td>
                  <Table.Td><Text size="sm">{wo.location ?? '-'}</Text></Table.Td>
                  <Table.Td>
                    {wo.departmentCode
                      ? <Badge size="xs" variant="light" color="indigo">{deptLabel(wo.departmentCode)}</Badge>
                      : <Text size="xs" c="dimmed">{t('workOrders.noDepartment')}</Text>}
                  </Table.Td>
                  <Table.Td><Badge size="xs" variant="light" color="gray">{catLabel(wo.category)}</Badge></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(wo.createdAt)}</Text></Table.Td>
                  <Table.Td>
                    <Tooltip label={wo.source === 'LINE' ? 'LINE' : 'Web'}>
                      {wo.source === 'LINE'
                        ? <IconBrandLine size={16} color="#06C755" />
                        : <IconWorld size={16} color="#94a3b8" />}
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLOR[wo.status]} variant="light" size="sm">
                      {t(`workOrders.status.${wo.status}`)}
                    </Badge>
                    {wo.status === 'IN_PROGRESS' && wo.assignedTo && (
                      <Text size="xs" c="dimmed" mt={2}>🔧 {wo.assignedTo}</Text>
                    )}
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Group gap={4}>
                      <Tooltip label={t('workOrders.viewDetails')}>
                        <ActionIcon variant="subtle" color="gray" onClick={() => openDetails(wo)}>
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      {(wo.status === 'OPEN' || wo.status === 'IN_PROGRESS') && (
                        <Tooltip label="แก้ไข">
                          <ActionIcon variant="subtle" color="violet" onClick={(e) => { e.stopPropagation(); handleOpenEdit(wo) }}>
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {wo.status === 'OPEN' && (
                        <Tooltip label={t('workOrders.claimButton')}>
                          <ActionIcon variant="subtle" color="blue" onClick={() => handleClaim(wo)}>
                            <IconTool size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {wo.status !== 'DONE' && wo.status !== 'CANCELLED' && (
                        <>
                          <Tooltip label={t('workOrders.closeButton')}>
                            <ActionIcon variant="subtle" color="lime" onClick={() => openClose(wo)}>
                              <IconCheck size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('workOrders.cancelButton')}>
                            <ActionIcon variant="subtle" color="red" onClick={() => openCancel(wo)}>
                              <IconX size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {filteredItems.length === 0 && (
            <Center py={40}><Text c="dimmed" size="sm">{t('workOrders.empty')}</Text></Center>
          )}
        </Box>
      )}

      {/* ── Modal: แก้ไขงาน ── */}
      <Modal opened={editOpened} onClose={closeEdit} size="md"
        title={<Text fw={600} style={{ fontSize: 18 }}>✏️ แก้ไขงาน #{editingWo?.id}</Text>}>
        <Stack gap="sm">
          <TextInput label={t('workOrders.colTitle')} value={editForm.title}
            onChange={(e) => setEditForm(p => ({ ...p, title: e.target.value }))} />
          <Textarea label={t('workOrders.formDescription')} minRows={2} value={editForm.description}
            onChange={(e) => setEditForm(p => ({ ...p, description: e.target.value }))} />
          <Autocomplete label={t('workOrders.colLocation')} data={locations}
            value={editForm.location}
            onChange={(v) => setEditForm(p => ({ ...p, location: v }))} />
          <Select label={t('workOrders.colDepartment')} placeholder={t('workOrders.departmentPlaceholder')}
            clearable searchable nothingFoundMessage={t('workOrders.noMatch')}
            data={departmentOptions} value={editForm.departmentCode || null}
            onChange={(v) => setEditForm(p => ({ ...p, departmentCode: v ?? '' }))} />
          <Select label={t('workOrders.colCategory')} searchable nothingFoundMessage={t('workOrders.noMatch')}
            data={categoryOptions(editForm.departmentCode, editForm.category)} value={editForm.category}
            onChange={(v) => setEditForm(p => ({ ...p, category: v ?? 'OTHER', departmentCode: (v && deptOfCategory(v)) || p.departmentCode }))} />
          <Checkbox label={t('workOrders.urgentLabel')} color="red" checked={editForm.isUrgent}
            onChange={(e) => setEditForm(p => ({ ...p, isUrgent: e.currentTarget.checked }))} />
          <Text size="xs" c="dimmed">การแก้ไขจะถูกบันทึกในประวัติงานพร้อมชื่อผู้แก้ไข</Text>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeEdit}>{t('common.cancel')}</Button>
            <Button loading={saving} onClick={handleSaveEdit}
              style={{ background: 'var(--brand-primary,#0f172a)', color: '#fff', fontWeight: 500 }}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Modal: สร้างงานใหม่ ── */}
      <Modal opened={createOpened} onClose={closeCreateModal} size={isSmall ? '100%' : 'md'}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{t('workOrders.newButton')}</Text>}>
        <Stack gap="sm">
          <TextInput label={t('workOrders.colTitle')} value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
          <Textarea label={t('workOrders.formDescription')} minRows={3} value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          <Autocomplete
            label={t('workOrders.colLocation')}
            placeholder={t('workOrders.locationPlaceholder', 'พิมพ์หรือเลือกห้อง/ตำแหน่ง')}
            data={locations}
            value={form.location}
            onChange={(v) => setForm((p) => ({ ...p, location: v }))}
          />
          <Select label={t('workOrders.colDepartment')} placeholder={t('workOrders.departmentPlaceholder')}
            clearable searchable nothingFoundMessage={t('workOrders.noMatch')}
            data={departmentOptions} value={form.departmentCode || null}
            onChange={(v) => setForm((p) => ({ ...p, departmentCode: v ?? '' }))} />
          <Select label={t('workOrders.colCategory')} searchable nothingFoundMessage={t('workOrders.noMatch')}
            data={categoryOptions(form.departmentCode, form.category)} value={form.category}
            onChange={(v) => setForm((p) => ({ ...p, category: v ?? 'OTHER', departmentCode: (v && deptOfCategory(v)) || p.departmentCode }))} />
          <Checkbox label={t('workOrders.urgentLabel')} color="red" checked={form.isUrgent}
            onChange={(e) => setForm((p) => ({ ...p, isUrgent: e.currentTarget.checked }))} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeCreateModal}>{t('common.cancel')}</Button>
            <Button loading={creating} onClick={handleCreate}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Modal: ปิดงาน ── */}
      <Modal opened={closeOpened} onClose={closeCloseModal} size={isSmall ? '100%' : 'sm'}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{t('workOrders.closeButton')} #{closingWo?.id}</Text>}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">{closingWo?.title}</Text>
          <Select
            label={t('workOrders.consumeItemLabel')}
            placeholder={t('workOrders.consumeItemPlaceholder')}
            clearable
            data={invItems.map((i) => ({ value: String(i.id), label: `${i.name} (${t('workOrders.stockRemaining')} ${i.stockQty} ${i.unit})` }))}
            value={closeItemId}
            onChange={setCloseItemId}
          />
          {closeItemId && (
            <NumberInput label={t('workOrders.consumeQtyLabel')} min={1} value={closeQty} onChange={(v) => setCloseQty(Number(v) || 1)} />
          )}
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeCloseModal}>{t('common.cancel')}</Button>
            <Button loading={closing} onClick={handleClose} color="lime">
              {t('workOrders.closeButton')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Modal: ยกเลิกงาน พร้อมเหตุผล ── */}
      <Modal opened={cancelOpened} onClose={closeCancelModal} size={isSmall ? '100%' : 'sm'}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{t('workOrders.cancelButton')} #{cancellingWo?.id}</Text>}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">{cancellingWo?.title}</Text>
          <Textarea label={t('workOrders.cancelReasonLabel')} minRows={3} value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder={t('workOrders.cancelReasonPlaceholder')} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeCancelModal}>{t('common.cancel')}</Button>
            <Button loading={cancelling} onClick={handleCancel} color="red">
              {t('workOrders.cancelButton')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Modal: รายละเอียด + ประวัติอัพเดต ── */}
      <Modal opened={detailOpened} onClose={closeDetailModal} size={isSmall ? '100%' : 'md'}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>#{detailWo?.id} {detailWo?.title}</Text>}>
        <Stack gap="md">
          <Group gap="xs">
            <Badge color={STATUS_COLOR[detailWo?.status]} variant="light">{t(`workOrders.status.${detailWo?.status}`)}</Badge>
            {detailWo?.location && <Badge variant="light" color="gray">{detailWo.location}</Badge>}
            <Badge variant="light" color="gray">{catLabel(detailWo?.category)}</Badge>
            {detailWo?.departmentCode && <Badge variant="light" color="indigo">{deptLabel(detailWo.departmentCode)}</Badge>}
          </Group>
          <Text size="xs" c="dimmed">{t('workOrders.colCreated')}: {fmtDateTime(detailWo?.createdAt)}</Text>

          {detailWo?.status === 'IN_PROGRESS' && detailWo?.assignedTo && (
            <Text size="sm" c="dimmed">🔧 {t('workOrders.claimedBy')} {detailWo.assignedTo}</Text>
          )}
          {detailWo?.status === 'OPEN' && (
            <Button size="xs" variant="light" color="blue" leftSection={<IconTool size={14} />}
              onClick={async () => { await handleClaim(detailWo); openDetails({ ...detailWo, status: 'IN_PROGRESS' }) }}>
              {t('workOrders.claimButton')}
            </Button>
          )}

          <Text size="sm">{detailWo?.description}</Text>

          {/* ── Materials / วัสดุที่ใช้ ── */}
          <Box>
            <Group justify="space-between" mb={8}>
              <Text size="xs" fw={700} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {t('workOrders.materialsTitle', 'วัสดุที่ใช้')}
              </Text>
              {detailWo?.status !== 'DONE' && detailWo?.status !== 'CANCELLED' && (
                <Button size="xs" variant="subtle" color="teal" leftSection={<IconPlus size={12} />}
                  onClick={async () => {
                    const item = invItems.find(i => {
                      const name = prompt(t('workOrders.materialItemPrompt', 'พิมพ์ชื่อของ (บางส่วน):') ?? '')
                      return name && i.name.toLowerCase().includes(name.toLowerCase())
                    })
                    if (!item) return
                    const qtyStr = prompt(`จำนวน (${item.unit}):`) ?? '1'
                    const qty = parseInt(qtyStr, 10) || 1
                    try {
                      await addWorkOrderMaterial(detailWo.id, { consumableItemId: item.id, qty })
                      const { data } = await getWorkOrderMaterials(detailWo.id)
                      setDetailMaterials(data)
                      notifications.show({ color: 'teal', message: `เพิ่ม ${item.name} x${qty}` })
                    } catch (e: any) {
                      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
                    }
                  }}>
                  {t('common.add')}
                </Button>
              )}
            </Group>
            {detailMaterials.length === 0 ? (
              <Text size="xs" c="dimmed">{t('workOrders.noMaterials', '(ยังไม่มีรายการวัสดุ)')}</Text>
            ) : (
              <Stack gap={4}>
                {detailMaterials.map((m) => (
                  <Group key={m.id} justify="space-between" gap={4}>
                    <Text size="sm">{m.consumableItem?.name ?? `#${m.consumableItemId}`} × {m.qty} {m.consumableItem?.unit ?? ''}</Text>
                    <Group gap={4}>
                      {m.stockDeducted ? <Badge size="xs" color="lime">ตัดสต็อกแล้ว</Badge> : <Badge size="xs" color="gray">รอตัดสต็อก</Badge>}
                      {detailWo?.status !== 'DONE' && detailWo?.status !== 'CANCELLED' && !m.stockDeducted && (
                        <ActionIcon size="xs" color="red" variant="subtle" onClick={async () => {
                          await removeWorkOrderMaterial(detailWo.id, m.id)
                          setDetailMaterials(prev => prev.filter(x => x.id !== m.id))
                        }}>
                          <IconX size={12} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Group>
                ))}
              </Stack>
            )}
          </Box>

          <Box>
            <Text size="xs" fw={700} c="dimmed" mb={8} style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t('workOrders.historyTitle')}
            </Text>
            {detailLoading ? (
              <Center py={20}><Loader size="sm" /></Center>
            ) : (
              <Timeline active={detailUpdates.length} bulletSize={20} lineWidth={2}>
                {detailUpdates.map((u) => (
                  <Timeline.Item key={u.id} bullet={<IconMessageCircle size={12} />}
                    title={<Text size="sm" fw={600}>{u.authorName ?? 'System'}</Text>}>
                    <Text size="xs" c="dimmed" mb={2}>{new Date(u.createdAt).toLocaleString('th-TH')}</Text>
                    <Text size="sm">{u.note}</Text>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </Box>

          {detailWo?.status !== 'DONE' && detailWo?.status !== 'CANCELLED' && (
            <Group align="flex-end" gap="xs">
              <Textarea flex={1} placeholder={t('workOrders.addNotePlaceholder')} minRows={1}
                value={newNote} onChange={(e) => setNewNote(e.target.value)} />
              <Button loading={addingNote} onClick={handleAddNote} disabled={!newNote.trim()}
                style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)' }}>
                {t('workOrders.addNoteButton')}
              </Button>
            </Group>
          )}
        </Stack>
      </Modal>
    </Box>
  )
}
